import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Package, Users, Cpu, HardDrive, MemoryStick, PauseCircle, PlayCircle } from 'lucide-react';
import { PackageService } from '../../services/packageService';
import type { ServerPackage } from '../../types/package';
import { PackageModal } from './PackageModal';

export function PackageManager() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<ServerPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packages, setPackages] = useState<ServerPackage[]>([]);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await PackageService.getAllPackages(undefined, true);
      setPackages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (pkg: ServerPackage) => {
    if (!pkg._id) return;
    try {
      await PackageService.updatePackage(pkg._id, { isActive: !pkg.isActive });
      await loadPackages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update package status');
    }
  };

  const openCreateForm = () => {
    setEditingPackage(null);
    setIsFormOpen(true);
  };

  const openEditForm = (pkg: ServerPackage) => {
    setEditingPackage(pkg);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingPackage(null);
  };

  const handleDelete = async (pkg: ServerPackage) => {
    if (!confirm(`Delete ${pkg.displayName}?`)) return;
    try {
      await PackageService.deletePackage(pkg._id!);
      await loadPackages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-100">Packages</h2>
          <p className="text-sm text-gray-500">Manage game server packages</p>
        </div>
        <button onClick={openCreateForm} className="btn-primary">
          <Plus className="w-4 h-4" /> Add Package
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={() => setError(null)} className="text-xs text-red-500 hover:text-red-400 mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Package Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {packages.map((pkg) => (
          <div key={pkg._id} className="card-glow p-5">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-dark-700 rounded-lg">
                  <Package className="w-5 h-5 text-neon-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-100 flex items-center gap-2">
                    {pkg.displayName}
                    {pkg.isFree && <span className="badge-online text-xs">FREE</span>}
                  </h3>
                  <p className="text-xs text-gray-500 capitalize">{pkg.game}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                <button onClick={() => handleStatus(pkg)} className="btn-ghost p-1.5" title={pkg.isActive ? 'Deactivate package' : 'Activate package'}>
                  {pkg.isActive ? <PauseCircle className="w-4 h-4 text-yellow-400" /> : <PlayCircle className="w-4 h-4 text-neon-400" />}
                </button>
                <button onClick={() => openEditForm(pkg)} className="btn-ghost p-1.5">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(pkg)} className="btn-ghost p-1.5 text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <p className="text-sm text-gray-400 mb-4 line-clamp-2">{pkg.description}</p>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2 text-gray-400">
                <Users className="w-4 h-4 text-gray-500" />
                <span>{pkg.maxPlayers} players</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <MemoryStick className="w-4 h-4 text-gray-500" />
                <span>{pkg.ram}GB RAM</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Cpu className="w-4 h-4 text-gray-500" />
                <span>{pkg.cpu ?? '?'} vCPU</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <HardDrive className="w-4 h-4 text-gray-500" />
                <span>{pkg.storage ?? '?'}GB storage</span>
              </div>
              <div className="text-neon-400 font-medium mt-1">
                {pkg.isFree ? 'Free' : `${pkg.price} RON`}
              </div>
              <div className="mt-1">
                <span className={pkg.isActive ? 'badge-online' : 'badge-offline'}>
                  {pkg.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {packages.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="font-medium text-gray-300 mb-2">No packages</h3>
          <p className="text-sm text-gray-500 mb-4">Create your first package to get started.</p>
          <button onClick={openCreateForm} className="btn-primary">
            <Plus className="w-4 h-4" /> Add Package
          </button>
        </div>
      )}

      {isFormOpen && (
        <PackageModal
          isOpen={isFormOpen}
          editingPackage={editingPackage}
          onClose={closeForm}
          onSaved={async () => { await loadPackages(); closeForm(); }}
        />
      )}
    </div>
  );
}
